import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/Input'
import { cn } from '@/utils/cn'
import {
  selectClasses, textareaClasses, labelClasses, submitButtonClasses, fieldError,
} from './formStyles'
import { fetchDecks, createDeck } from '@/api/decks'
import { translateText } from '@/api/translations'
import type { Deck } from '@/types/deck'
import { focusRing, quietFill, softFill } from '@/components/ui/buttonStyles'

const NEW_DECK_VALUE = '__new__'

export interface PhraseValues {
  phrase: string
  translation: string
  notes: string
  deck_id: number | null
}

interface PhraseFormProps {
  languageId: number
  initial: PhraseValues
  submitLabel: string
  submitting: boolean
  errors: Record<string, string[]>
  autoTranslate?: boolean
  onSubmit: (values: PhraseValues) => void
}

/** Create/edit form for a phrase card: phrase + translation (with AI prefill) + deck. */
export function PhraseForm({
  languageId, initial, submitLabel, submitting, errors, autoTranslate, onSubmit,
}: PhraseFormProps) {
  const [values, setValues] = useState<PhraseValues>(initial)
  const [decks, setDecks] = useState<Deck[]>([])
  const [creatingDeck, setCreatingDeck] = useState(false)
  const [newDeckName, setNewDeckName] = useState('')
  const [deckError, setDeckError] = useState<string | null>(null)
  const [translating, setTranslating] = useState(false)
  const autoTranslated = useRef(false)

  useEffect(() => {
    fetchDecks(languageId)
      .then((res) => setDecks(res.data.data))
      .catch(() => setDecks([]))
  }, [languageId])

  useEffect(() => {
    if (!autoTranslate || autoTranslated.current) return
    if (!initial.phrase.trim() || initial.translation) return
    autoTranslated.current = true
    runTranslate(initial.phrase)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function set<K extends keyof PhraseValues>(key: K, value: PhraseValues[K]) {
    setValues((v) => ({ ...v, [key]: value }))
  }

  function runTranslate(text: string) {
    if (!text.trim() || translating) return
    setTranslating(true)
    translateText(languageId, text.trim())
      .then((res) => set('translation', res.data.translation))
      .catch(() => undefined)
      .finally(() => setTranslating(false))
  }

  function handleCreateDeck() {
    const name = newDeckName.trim()
    if (!name) return
    setDeckError(null)
    createDeck(languageId, name)
      .then((res) => {
        const deck = res.data.data
        setDecks((prev) => [...prev, deck])
        set('deck_id', deck.id)
        setCreatingDeck(false)
        setNewDeckName('')
      })
      .catch(() => setDeckError('Could not create the deck.'))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit(values)
  }

  const phraseError = fieldError(errors, 'lemma', 'Phrase')

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="phrase-text" className={labelClasses}>Phrase</label>
        <textarea
          id="phrase-text"
          value={values.phrase}
          onChange={(e) => set('phrase', e.target.value)}
          rows={2}
          required
          aria-invalid={phraseError ? true : undefined}
          aria-describedby={phraseError ? 'phrase-text-error' : undefined}
          className={textareaClasses}
        />
        {phraseError && (
          <p id="phrase-text-error" role="alert" className="text-xs text-error font-light">
            {phraseError}
          </p>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            id="phrase-translation"
            label="Translation"
            value={translating ? 'Translating…' : values.translation}
            onChange={(e) => set('translation', e.target.value)}
            error={fieldError(errors, 'translation')}
            disabled={translating}
            required
          />
        </div>
        <button
          type="button"
          onClick={() => runTranslate(values.phrase)}
          disabled={translating || !values.phrase.trim()}
          className={cn(
            'shrink-0 px-4 py-3 rounded-md text-xs font-light min-h-11',
            'border border-border-divider text-accent hover:bg-surface-bg',
            'transition-colors duration-200 disabled:opacity-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
          )}
        >
          Translate
        </button>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="phrase-deck" className={labelClasses}>Deck</label>
        {!creatingDeck ? (
          <select
            id="phrase-deck"
            value={values.deck_id ?? ''}
            onChange={(e) => {
              if (e.target.value === NEW_DECK_VALUE) {
                setCreatingDeck(true)
                return
              }
              set('deck_id', e.target.value ? Number(e.target.value) : null)
            }}
            required
            className={selectClasses}
          >
            <option value="">Choose a deck…</option>
            {decks.map((deck) => (
              <option key={deck.id} value={deck.id}>{deck.name}</option>
            ))}
            <option value={NEW_DECK_VALUE}>+ New deck…</option>
          </select>
        ) : (
          <div className="flex items-center gap-2">
            <input
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              placeholder="Deck name"
              aria-label="New deck name"
              className={cn(selectClasses, 'flex-1')}
            />
            <button
              type="button"
              onClick={handleCreateDeck}
              className={`shrink-0 text-xs font-normal transition-colors duration-200 min-h-11 px-4 rounded-full ${softFill} ${focusRing}`}
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setCreatingDeck(false)}
              className={`shrink-0 text-xs font-normal transition-colors duration-200 min-h-11 px-4 rounded-full ${quietFill} ${focusRing}`}
            >
              Cancel
            </button>
          </div>
        )}
        {(deckError ?? fieldError(errors, 'deck', 'Deck')) && (
          <p role="alert" className="text-xs text-error font-light">
            {deckError ?? fieldError(errors, 'deck', 'Deck')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="phrase-notes" className={labelClasses}>Notes</label>
        <textarea
          id="phrase-notes"
          value={values.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className={textareaClasses}
        />
      </div>

      {errors.base && (
        <p role="alert" className="text-xs text-error font-light">{errors.base[0]}</p>
      )}

      <div className="flex items-center justify-end mt-1">
        <button type="submit" disabled={submitting || translating} className={submitButtonClasses}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}
